// Based on https://observablehq.com/@saneef/radar-chart
class RadarChart {
  constructor(container) {
    this._container = container;

    this._data = null;
    this._chartData = [];
    this._width = 640;
    this._height = 640;
    this._margins = {
      top: 60,
      right: 60,
      bottom: 60,
      left: 60,
    };
    this._radius = 0;
    this._boundedRadius = 0;
    this._boundedWidth = 0;
    this._boundedHeight = 0;

    this._dotRadius = 4;
    this._baseAngleOffset = -0.5 * Math.PI;
    this._angleOffset = 0;

    this._axesLabels = new Set();
    this._labelWidth = 120;

    this._field = {
      axis: "axis",
      value: "value",
    };

    this._maxValue = 10;
    this._bg = undefined;
    this._curve = d3.curveLinearClosed;
    this._palette = palette;

    this._strokeDasharray = "2 2";
    this._fontFamily = "system-ui";

    this._axisAccessor = (d) => {
      // console.log(d, d[this._field.axis]);
      return d[this._field.axis];
    };

    this._valueAccessor = (d) => {
      // console.log(d, d[this._field.value]);
      return d[this._field.value];
    };

    this._getCoordinatesForAngle = this._getCoordinatesForAngle.bind(this);
  }

  data(_) {
    return arguments.length ? ((this._data = _), this) : this._data;
  }

  field(_) {
    return arguments.length ? ((this._field = _), this) : this._field;
  }

  margins(_) {
    if (typeof _ === "number") {
      return (
        (this._margins = {
          top: _,
          right: _,
          bottom: _,
          left: _,
        }),
        this
      );
    }
    return arguments.length ? ((this._margins = _), this) : this._margins;
  }

  palette(_) {
    return arguments.length ? ((this._palette = _), this) : this._palette;
  }

  curve(_) {
    return arguments.length ? ((this._curve = _), this) : this._curve;
  }

  fontFamily(_) {
    return arguments.length ? ((this._fontFamily = _), this) : this._fontFamily;
  }

  angleOffset(_) {
    return arguments.length
      ? ((this._angleOffset = _), this)
      : this._angleOffset;
  }

  size(w, h) {
    return arguments.length
      ? ((this._width = w), (this._height = h), this)
      : this._field;
  }

  _radiusFromSize(w, h) {
    return Math.floor(Math.min(w, h) / 2);
  }

  _updateBounds() {
    this._boundedWidth =
      this._width - (this._margins.left + this._margins.right);
    this._boundedHeight =
      this._height - (this._margins.top + this._margins.top);

    this._radius = this._radiusFromSize(this._width, this._height);
    this._boundedRadius =
      this._radius -
      Math.max(
        this._margins.top + this._margins.bottom,
        this._margins.left + this._margins.right
      ) /
        2;

    this._bounds = this._container
      .append("g")
      .style(
        "transform",
        `translate(${this._margins.left + this._boundedWidth / 2}px, ${
          this._margins.top + this._boundedHeight / 2
        }px)`
      );

    // this._bounds
    //   .append("rect")
    //   .attr("width", this._boundedWidth)
    //   .attr("height", this._boundedHeight)
    //   .attr("x", -this._boundedRadius)
    //   .attr("y", -this._boundedRadius);
  }

  _getCoordinatesForAngle(angle, r = this._boundedRadius, offset = 0) {
    return [Math.cos(angle) * (r + offset), Math.sin(angle) * (r + offset)];
  }

  _process() {
    this._chartData = [...this._data];
  }

  _init() {
    this._updateBounds();

    // Because of survey v1 and v2 data gets in here a bit differently
    // in v1 the data is an array of objects
    // in v2 the data is an array of arrays (with objects inside)
    let chartData = this._chartData;
    // test if the data is an array of arrays
    if (Array.isArray(this._chartData[0])) {
      chartData = this._chartData[0];
    }

    // Scales
    // console.log(chartData);
    this._axesLabels = chartData.map(this._axisAccessor);

    const effectiveAngleOffset = this._baseAngleOffset + this._angleOffset;

    this._angleScale = d3
      .scaleBand()
      .domain(this._axesLabels)
      .range([effectiveAngleOffset, Math.PI * 2 + effectiveAngleOffset]);

    const maxValue = Math.max(
      this._maxValue,
      ...chartData.map(this._valueAccessor)
    );

    this._radiusScale = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([0, this._boundedRadius]);

    // Curves
    this._radarLine = d3
      .lineRadial()
      .curve(this._curve)
      .radius((d) => this._radiusScale(this._valueAccessor(d)))
      .angle((d) => Math.PI / 2 + this._angleScale(this._axisAccessor(d)));
  }

  _renderBg() {
    this._container
      .insert("rect", ":first-child")
      .attr("fill", this._palette.bg)
      .attr("width", this._width)
      .attr("height", this._height);
  }

  _renderAxis() {
    // Because of survey v1 and v2 data gets in here a bit differently
    // in v1 the data is an array of objects
    // in v2 the data is an array of arrays (with objects inside)
    let chartData = this._chartData;
    // test if the data is an array of arrays
    if (Array.isArray(this._chartData[0])) {
      chartData = this._chartData[0];
    }

    const peripherals = this._bounds.append("g").attr("class", "peripherals");

    // Add tick circles
    const ticks = this._radiusScale.ticks(5);
    ticks.forEach((r) => {
      if (!r) return;

      const tick = peripherals.append("g");

      tick
        .append("g")
        .attr("fill", this._palette.grid)
        .attr("fill-opacity", 0)
        .attr("stroke", this._palette.grid)
        .append("path")
        .attr("d", () =>
          polygon(chartData.length).scale(this._radiusScale(r))()
        );

      // tick
      //   .append("circle")
      //   .attr("class", "tick-circle")
      //   .attr("r", this._radiusScale(r))
      //   .attr("fill", "none")
      //   .attr("stroke", this._palette.grid)
      //   .attr("stroke-dasharray", this._strokeDasharray);
      const [_, max] = this._radiusScale.domain();

      // if (r >= max) return; // Don't draw last tick label
      tick
        .append("text")
        .attr("x", 3)
        .attr("y", -this._radiusScale(r) - 4)
        .attr("class", "tick-label")
        .style("fill", this._palette.text)
        .style("fill-opacity", 0.5)
        .style("font-family", this._fontFamily)
        .style("font-size", "0.75rem")
        .text(r);
    });

    const getCoords = (d) => this._getCoordinatesForAngle(this._angleScale(d));

    // Add grid lines
    peripherals
      .selectAll("line")
      .data(this._axesLabels)
      .join("line")
      .attr("stroke-dasharray", this._strokeDasharray)
      .style("stroke", this._palette.grid)
      .each(function (d) {
        const [x2, y2] = getCoords(d);
        d3.select(this).attr("x2", x2).attr("y2", y2);
      });

    // Add Axis labels
    const labelRadiusScale = 70;
    const getCoordsForText = (d) =>
      this._getCoordinatesForAngle(
        this._angleScale(d),
        this._boundedRadius,
        labelRadiusScale
      );
    setTimeout(() => {
      let cfg = this;
      peripherals
        .append("g")
        .attr("class", "axis-labels")
        .selectAll("text")
        .data(this._axesLabels)
        .join("g")
        .each(function (d) {
          const [x, y] = getCoordsForText(d);
          d3.select(this)
            .attr("transform", `translate(${x}, ${y})`)
            // .attr("x", x)
            // .attr("y", Math.abs(x) < 5 && y < 0 ? y : y + 30)
            .style(
              "text-anchor",
              Math.abs(x) < 5 ? "middle" : x > 0 ? "start" : "end"
            )
            .append("image")
            .attr("href", `/img/iph/${d.toLowerCase()}.png`) // Set the image source URL
            .attr("width", cfg._labelWidth) // Set the image width
            .attr("height", cfg._labelWidth) // Set the image height
            .attr("x", (-1 * cfg._labelWidth) / 2)
            .attr("y", (-1 * cfg._labelWidth) / 2)
            .attr("alt", "Banana icon"); // Set the image alt text
        })
        // .text((d) => d)
        .style("fill", this._palette.text)
        .style("font-family", this._fontFamily)
        .style("font-size", "0.7rem")
        .style("font-weight", "medium")
        .style("dominant-baseline", "middle")
        .attr("dy", "0em");
      // .call(wrap, this._labelWidth)
    });
  }

  _renderPlots() {
    const plots = this._bounds.append("g");

    const handleSingleChart = (singleChart, index) => {
      let plot = plots.append("g");

      // Add curve
      plot
        .append("g")
        .attr("fill", this._palette.line[index])
        .attr("fill-opacity", 0.15)
        .attr("stroke", this._palette.line[index])
        .append("path")
        .attr("d", () => this._radarLine(singleChart));

      // Add dots
      const getCoordsForPlot = (d) =>
        this._getCoordinatesForAngle(
          this._angleScale(this._axisAccessor(d)),
          this._radiusScale(this._valueAccessor(d))
        );

      plot
        .append("g")
        .attr("fill", this._palette.dot[index])
        .selectAll("circle")
        .data(singleChart)
        .join("circle")
        .attr("r", this._dotRadius)
        .each(function (d) {
          const [cx, cy] = getCoordsForPlot(d);
          d3.select(this).attr("cx", cx).attr("cy", cy);
        })
        .attr(
          "title",
          (d) => `${this._axisAccessor(d)}: ${this._valueAccessor(d)}`
        );
    };

    // Because of survey v1 and v2 data gets in here a bit differently
    // in v1 the data is an array of objects
    // in v2 the data is an array of arrays (with objects inside)
    // test if the data is an array of arrays
    if (Array.isArray(this._chartData[0])) {
      // foreach it
      this._chartData.forEach((singleChart, index) => {
        handleSingleChart(singleChart, index);
      });
    } else {
      // if not, then we have a single chart
      handleSingleChart(this._chartData, 0);
    }
  }

  render() {
    this._process();
    this._init();
    this._renderBg();
    this._renderAxis();
    this._renderPlots();
    return this;
  }
}

// https://observablehq.com/@jacobtfisher/brand-identity-radar-chart
function wrap(text, width) {
  text.each(function () {
    var text = d3.select(this),
      words = text.text().split(/\s+/).reverse(),
      word,
      line = [],
      lineNumber = 0,
      lineHeight = 1.4, // ems
      y = text.attr("y"),
      x = text.attr("x"),
      dy = parseFloat(text.attr("dy")),
      tspan = text
        .text(null)
        .append("tspan")
        .attr("x", x)
        .attr("y", y)
        .attr("dy", dy + "em");

    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(" "));
      if (tspan.node().getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(" "));
        line = [word];
        tspan = text
          .append("tspan")
          .attr("x", x)
          .attr("y", y)
          .attr("dy", ++lineNumber * lineHeight + dy + "em")
          .text(word);
      }
    }
  });
} //wrap

let palette = {
  text: "#222",
  grid: "#d3d3d3",
  gridAccent: "#FFFFFF",
  dot: ["#0099D8", "#ffb700"],
  line: ["#6DCFF6", "#ffd900"],
  bg: "#ffffff",
};

function polygon(sides) {
  var length = sides,
    s = 1,
    phase = 0;
  const radial = d3
    .lineRadial()
    .curve(d3.curveLinearClosed)
    .angle((_, i) => (i / length) * 2 * Math.PI + phase)
    .radius(() => s);
  const poly = function () {
    return radial(Array.from({ length }));
  };
  poly.context = function (_) {
    return arguments.length ? (radial.context(_), poly) : radial.context();
  };
  poly.n = function (_) {
    return arguments.length ? ((length = +_), poly) : length;
  };
  poly.rotate = function (_) {
    return arguments.length ? ((phase = +_), poly) : phase;
  };
  poly.scale = function (_) {
    return arguments.length ? ((s = +_), poly) : s;
  };
  poly.curve = function (_) {
    return arguments.length ? (radial.curve(_), poly) : radial.curve();
  };
  poly.radius = radial.radius;
  poly.angle = radial.angle;
  return poly;
}

export { RadarChart };
